/**
 * Pure helpers behind FindReplaceDirective.
 *
 * Deliberate asymmetry, per the clinical requirement: the *find* term is matched
 * case-insensitively, but the *replacement* is inserted verbatim — no case
 * folding, no trimming, no whitespace normalisation. Everything outside a match
 * is copied across untouched, so indentation, blank lines and trailing spaces in
 * a note survive a replace exactly as the clinician left them.
 */

export interface FindMatch {
  start: number;
  /** Exclusive. */
  end: number;
}

export interface ReplacePatch {
  start: number;
  /** Exclusive. */
  end: number;
  text: string;
}

export function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Every case-insensitive occurrence of `term`, in document order. The term is
 * treated as a literal string, so a clinician searching for "5mg (PO)" gets the
 * text they typed rather than a regex.
 */
export function findMatches(text: string, term: string): FindMatch[] {
  if (!text || !term) {
    return [];
  }

  const matches: FindMatch[] = [];
  // A literal, escaped, non-empty term always consumes at least one character,
  // so lastIndex advances on every iteration and this cannot spin.
  const matcher = new RegExp(escapeRegExp(term), 'gi');
  let match: RegExpExecArray | null;

  while ((match = matcher.exec(text)) !== null) {
    matches.push({ start: match.index, end: match.index + match[0].length });
  }

  return matches;
}

/** Index of the first match starting at or after `position`, wrapping to 0. */
export function matchIndexAt(matches: FindMatch[], position: number): number {
  if (!matches.length) {
    return -1;
  }
  const index = matches.findIndex(match => match.start >= position);
  return index === -1 ? 0 : index;
}

/**
 * Builds the marked-up copy of the note that sits behind the textarea. All text
 * is HTML-escaped here; the only markup that reaches the DOM is the `<mark>`
 * wrappers this function emits.
 */
export function buildHighlightHtml(text: string, matches: FindMatch[], activeIndex: number): string {
  if (!matches.length) {
    return escapeHtml(text);
  }

  let html = '';
  let cursor = 0;

  matches.forEach((match, index) => {
    html += escapeHtml(text.slice(cursor, match.start));
    const active = index === activeIndex ? ' app-fr-hit--active' : '';
    html += `<mark class="app-fr-hit${active}">${escapeHtml(text.slice(match.start, match.end))}</mark>`;
    cursor = match.end;
  });

  return html + escapeHtml(text.slice(cursor));
}

/**
 * Collapses every match into a single edit spanning the first match to the last,
 * so "replace all" is one entry on the browser's undo stack rather than N. The
 * text between matches is carried over character-for-character.
 */
export function buildReplaceAllPatch(
  text: string,
  matches: FindMatch[],
  replacement: string
): ReplacePatch | null {
  if (!matches.length) {
    return null;
  }

  const start = matches[0].start;
  const end = matches[matches.length - 1].end;
  let patched = '';
  let cursor = start;

  for (const match of matches) {
    patched += text.slice(cursor, match.start) + replacement;
    cursor = match.end;
  }

  return { start, end, text: patched };
}
